/**
 * Owner: Masa Bayu
 * File: MarketplaceDetailHero.tsx
 * Date:Time: 2026-10-23 01:05
 * Description: MarketplaceDetail §377 — bahagian 1 · HERO PRODUCT.
 *   §466 S1 — lencana/subtitle/rating rekaan dibuang; eyebrow, penulis, bahasa
 *   dan harga hanya dipapar bila manifest hub benar-benar menghantar medan itu.
 *   Harga ikut kod mata wang descriptor (bukan profil pelawat).
 */

import type { ReactElement } from 'react';
import { LandingIcon } from './landing/LandingIcons';
import {
  formatMarketplacePriceOrUnknown,
  languageDisplayLabel,
  type MarketplaceLabels,
} from './marketplace-locale';
import type { MarketplaceBook } from './marketplace-projection';
import { slugifyAuthorName } from '../../author/public/public-author-data';
import { qxCategoryName } from '@qx/qxread-core';
import { useHubCoverSrc } from '../../publication/use-hub-cover';

export interface MarketplaceDetailHeroProps {
  readonly t: MarketplaceLabels;
  readonly book: MarketplaceBook;
  readonly shared: boolean;
  /** §466 S2 — CTA Beli hanya ada bila terbitan ini ada tawaran hub. */
  readonly purchasable: boolean;
  readonly onBuy: () => void;
  readonly onReadSample: () => void;
  readonly onShare: () => void;
  readonly onNavigate: (path: string) => void;
}

export function MarketplaceDetailHero({
  t,
  book,
  shared,
  purchasable,
  onBuy,
  onReadSample,
  onShare,
  onNavigate,
}: MarketplaceDetailHeroProps): ReactElement {
  const categoryName =
    book.categoryId === null ? null : qxCategoryName(book.categoryId, t.language);
  const subcategoryName =
    book.subcategoryId === null ? null : qxCategoryName(book.subcategoryId, t.language);
  const eyebrow =
    categoryName === null && subcategoryName === null
      ? null
      : categoryName === null
        ? subcategoryName
        : subcategoryName === null
          ? categoryName
          : `${categoryName} › ${subcategoryName}`;
  const authorProfilePath =
    book.author === null ? null : `/author/${encodeURIComponent(slugifyAuthorName(book.author))}`;
  const coverSrc = useHubCoverSrc(book.publicationId);
  return (
    <div className="hmk__detail-hero">
      <div className="hmk__detail-cover" data-tone={book.coverTone} aria-hidden="true">
        {/* COVER-A3 — cover sebenar dari bundle; kotak nada hanya lalai bila tiada cover. */}
        {coverSrc === null ? (
          <>
            <span className="hmk__cover-title">{book.title}</span>
            <span className="hmk__cover-pattern" />
          </>
        ) : (
          <img className="hmk__cover-img" src={coverSrc} alt="" />
        )}
      </div>

      <div className="hmk__detail-info">
        {eyebrow === null ? null : <p className="hmk__detail-eyebrow">{eyebrow.toUpperCase()}</p>}
        <h1 className="hmk__detail-heading">{book.title}</h1>
        {authorProfilePath === null ? null : (
          <p className="hmk__detail-author">
            {t.detail.byPrefix}{' '}
            <a
              href={authorProfilePath}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(authorProfilePath);
              }}
            >
              {book.author}
            </a>
          </p>
        )}
        <p className="hmk__detail-meta">
          <span className="hmk__ebook-chip">
            <LandingIcon name="book" size={13} /> eBook
          </span>
          {book.languageRef === null ? null : (
            <span className="hmk__detail-category">
              {languageDisplayLabel(book.languageRef, t.language)}
            </span>
          )}
        </p>
        <p className="hmk__detail-price">
          {formatMarketplacePriceOrUnknown(
            book.priceMinorUnits,
            book.currency,
            t.card.priceUnknown,
          )}
        </p>
        <div className="hmk__detail-ctas">
          {purchasable ? (
            <button type="button" className="hmk__buy-btn" onClick={onBuy}>
              <LandingIcon name="cart" size={15} /> {t.detail.buyEbook}
            </button>
          ) : null}
          <button type="button" className="hmk__buy-btn hmk__buy-btn--ghost" onClick={onReadSample}>
            {t.detail.readSample}
          </button>
          <button
            type="button"
            className="hmk__buy-btn hmk__buy-btn--ghost"
            data-added={shared}
            onClick={onShare}
          >
            {shared ? (
              <>
                <LandingIcon name="check" size={14} /> {t.detail.linkCopied}
              </>
            ) : (
              <>
                <LandingIcon name="share" size={14} /> {t.detail.share}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
